import { WarningIcon } from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { Table } from '../../components/table/table.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { Routes } from '../../routes.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { exposureChip } from '../../utils/threat-model/exposure-tone.css.ts'
import { CountDetailTooltip } from './count-detail-tooltip.tsx'
import { type FreshnessBand, freshnessBandLabels } from './estate-freshness.ts'
import type { RepositoryCoverage } from './org-threat-posture.ts'
import * as styles from './security-posture-page.css.ts'

/** Capitalised so the cell matches the "Yes" and "Partial" the estate figures name. */
const capitalise = (value: string): string => {
  const trimmed = value.trim()
  return trimmed.length > 0 ? `${trimmed[0]?.toUpperCase()}${trimmed.slice(1)}` : 'Unknown'
}

/** The shared `Table` puts `meta.className` on the cells but not the header, so the header centres itself. */
const centredHeader = (label: string) => () => <div className={styles.centredColumn}>{label}</div>

/**
 * How old the model behind a row is, when it is old enough to change how the row reads.
 *
 * A repository ranked worst-covered on a year-old document is a weaker claim than the same ranking on one
 * generated this week, and nothing else in this table says so. Marked rather than columned: the table is
 * about coverage, and an age on every row would compete with the figures it exists to show while most of
 * those ages need no action.
 */
export type ModelFreshness = {
  bandByRepository: Record<string, FreshnessBand>
  staleAfterDays: number
  recentWithinDays: number
}

/**
 * The stale value carries the warning and the way out; every other band is just its label.
 *
 * Only this one is worth interrupting a scan of the column for — the rest are reassurance, and an icon on
 * every row would spend the reader's attention evenly across rows that do not need it equally.
 */
const StaleValue: React.FC<{ repositoryId: string; label: string; staleAfterDays: number }> = ({
  repositoryId,
  label,
  staleAfterDays,
}) => (
  <TooltipWithHover
    trigger={
      <span className={styles.staleValue}>
        <WarningIcon size={16} weight="fill" aria-hidden />
        {label}
      </span>
    }
    side="top"
  >
    <span className={styles.staleTooltip}>
      <span className={styles.staleTooltipTitle}>Stale</span>
      <span className={styles.staleTooltipBody}>
        {`This information is based on a threat model over ${staleAfterDays} ${pluralize(staleAfterDays, 'day', 'days')} old.`}
      </span>
      {/* Radix keeps tooltip content hoverable by default, so this stays reachable on the way to it. */}
      <Link
        className={styles.staleTooltipLink}
        to={`${Routes.RepositoryDetailsPage.createPath({ repositoryId })}?tab=threat-model&threatModelView=overview`}
      >
        View model
      </Link>
    </span>
  </TooltipWithHover>
)

const columnHelper = createColumnHelper<RepositoryCoverage>()

const buildColumns = (freshness?: ModelFreshness): ColumnDef<RepositoryCoverage>[] =>
  [
    columnHelper.accessor('name', {
      header: 'Repository',
      cell: ({ row }) => (
        <Link
          className={styles.repoLink}
          to={`${Routes.RepositoryDetailsPage.createPath({ repositoryId: row.original.repositoryId })}?tab=threat-model&threatModelView=surface`}
        >
          {row.original.name}
        </Link>
      ),
      meta: { width: '30%', className: typography.paragraph },
    }),
    columnHelper.accessor('internetFacing', {
      header: centredHeader('Internet-facing'),
      cell: ({ row }) => (
        <span
          className={exposureChip({
            internetFacing: row.original.internetFacing,
            weight: 'bold',
            density: 'compact',
          })}
        >
          {capitalise(row.original.internetFacing)}
        </span>
      ),
      meta: { width: '16%', className: styles.centredColumn },
    }),
    columnHelper.accessor('vulnerabilityCount', {
      header: centredHeader('Vulnerabilities'),
      cell: ({ row }) => <span className={styles.numericCell}>{row.original.vulnerabilityCount}</span>,
      meta: { width: '12%', className: styles.centredColumn },
    }),
    columnHelper.accessor('gapCount', {
      header: centredHeader('Coverage gaps'),
      cell: ({ row }) => (
        <CountDetailTooltip
          heading={`No control in ${row.original.name}`}
          triggerLabel={`${row.original.gapCount} ${pluralize(row.original.gapCount, 'vulnerability', 'vulnerabilities')} with no control in ${row.original.name}`}
          unit={{ one: 'vulnerability', many: 'vulnerabilities' }}
          items={row.original.gapVulnerabilities.map(name => ({ key: name, name }))}
        >
          <span className={row.original.gapCount > 0 ? styles.gapCount : styles.numericCell}>
            {row.original.gapCount}
          </span>
        </CountDetailTooltip>
      ),
      meta: { width: '12%', className: styles.centredColumn },
    }),
    columnHelper.accessor('undefendedShare', {
      header: centredHeader('Undefended'),
      cell: ({ row }) => (
        <span className={styles.numericCell}>{`${Math.round(row.original.undefendedShare * 100)}%`}</span>
      ),
      meta: { width: '14%', className: styles.centredColumn },
    }),
    columnHelper.display({
      id: 'modelFreshness',
      header: centredHeader('Model freshness'),
      cell: ({ row }) => {
        if (freshness === undefined) {
          return null
        }

        // A listed repository with no band recorded reads as unknown rather than as fresh: this table only
        // lists repositories that have a model, so an absent band means the age is unknown, not recent.
        const band = freshness.bandByRepository[row.original.repositoryId] ?? 'undated'
        const label = freshnessBandLabels(freshness)[band]

        return band === 'stale' ? (
          <StaleValue
            repositoryId={row.original.repositoryId}
            label={label}
            staleAfterDays={freshness.staleAfterDays}
          />
        ) : (
          <span className={styles.freshnessValue}>{label}</span>
        )
      },
      meta: { width: '16%', className: styles.centredColumn },
    }),
  ] as ColumnDef<RepositoryCoverage>[]

export const LeastDefendedTable: React.FC<{
  repositories: RepositoryCoverage[]
  modelFreshness?: ModelFreshness
}> = ({ repositories, modelFreshness }) => {
  // The column definitions close over `modelFreshness`, so they are rebuilt when it changes and only then.
  const columns = useMemo(() => buildColumns(modelFreshness), [modelFreshness])

  return (
    <Table
      tableName="Least defended repositories"
      data={repositories}
      columns={columns}
      getRowId={row => row.repositoryId}
      emptyStateMessage="Every modelled repository has a control against each of its vulnerabilities."
    />
  )
}
